// Certificado fisioterapéutico e Indicaciones (receta) — documento A4 imprimible, mismo formato que los modelos de la clínica.
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const nl2br = (v) => esc(v).replace(/\r?\n/g, "<br>");
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const fechaLarga = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return "";
  return `${Number(m[3])} de ${MESES[Number(m[2]) - 1]} de ${m[1]}`;
};
const fechaCorta = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
};
const lineasLista = (txt) => String(txt || "").split(/\r?\n/).map((x) => x.trim()).filter(Boolean);

export function imprimirDocumentoClinico({ tipo, empresa, paciente, indicacion, firmante }) {
  const clinica = empresa?.nombre_fantasia || empresa?.nombre || empresa?.razon_social || "Clínica";
  const ciudad = empresa?.ciudad || "Asunción";
  const hoy = new Date();
  const iso = (indicacion?.fecha || `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`).slice(0, 10);
  const sexo = paciente?.sexo === "M" ? "Masculino" : paciente?.sexo === "F" ? "Femenino" : "";
  const sexoEc = [sexo, paciente?.estado_civil].filter(Boolean).join(" / ");
  const edad = paciente?.edad != null ? `${paciente.edad} años` : "";
  const firma = firmante?.firma ? `<img class="firma" src="${esc(firmante.firma)}" alt="firma">` : '<div style="height:70px"></div>';
  const generado = `${clinica} | Documento generado el ${fechaCorta(iso === "" ? "" : `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`)} ${String(hoy.getHours()).padStart(2, "0")}:${String(hoy.getMinutes()).padStart(2, "0")}`;

  let cuerpo = "";
  if (tipo === "certificado") {
    const ejercicios = lineasLista(indicacion?.ejercicios).map((l) => `<p class="item">${esc(l)}</p>`).join("");
    cuerpo = `
      <h1 class="clinica rosa">${esc(clinica)}</h1><div class="linea-rosa"></div>
      <h2 class="titulo rosa">CERTIFICADO FISIOTERAPÉUTICO</h2>
      <div class="sub verde">INDICACIONES</div>
      <p class="fecha">${esc(ciudad)}, ${esc(fechaLarga(iso))}</p>
      <table class="datos">
        <tr><th>Paciente</th><td>${esc(paciente?.nombre)}</td></tr>
        <tr><th>Edad</th><td>${esc(edad)}</td></tr>
        <tr><th>CI/RUC</th><td>${esc(paciente?.ruc)}</td></tr>
        <tr><th>Teléfono</th><td>${esc(paciente?.telefono)}</td></tr>
        <tr><th>F. Nacimiento</th><td>${esc(fechaCorta(paciente?.fecha_nacimiento))}</td></tr>
        <tr><th>Sexo / E. Civil</th><td>${esc(sexoEc)}</td></tr>
      </table>
      <div class="bloque"><div class="cap">DIAGNÓSTICO / MOTIVO DE CONSULTA</div><div>${nl2br(indicacion?.diagnostico)}</div></div>
      ${ejercicios ? `<div class="bloque"><div class="cap">PLAN DE EJERCICIOS / INDICACIONES</div>${ejercicios}</div>` : ""}
      ${indicacion?.instrucciones ? `<div class="bloque"><div class="cap">INSTRUCCIONES ADICIONALES</div><div>${nl2br(indicacion.instrucciones)}</div></div>` : ""}
      <div class="sep"></div>
      <div class="firmante">
        ${firma}
        <div class="rayafirma"></div>
        <div>${esc(firmante?.nombre)}</div>
        <div class="rosa chico">${esc(firmante?.especialidad || "Fisioterapeuta")}</div>
        <div class="chico">${esc(firmante?.titulo)}</div>
        ${firmante?.matricula ? `<div class="chico">Matrícula: ${esc(firmante.matricula)}</div>` : ""}
      </div>
      <div class="pie">${esc(generado)}</div>`;
  } else {
    cuerpo = `
      <h1 class="clinica rosa grande">${esc(clinica)}</h1>
      <div class="sub verde centro">Indicaciones</div>
      <div class="sep negra"></div>
      <p><b>Fecha:</b> ${esc(fechaCorta(iso))}</p>
      <table class="datos2">
        <tr><th>Paciente:</th><td>${esc(paciente?.nombre)}</td></tr>
        <tr><th>Edad:</th><td>${esc(edad)}</td></tr>
        <tr><th>Cédula:</th><td>${esc(String(paciente?.ruc || "").split("-")[0])}</td></tr>
      </table>
      <h3 class="verde">Diagnóstico</h3><p>${nl2br(indicacion?.diagnostico)}</p>
      ${indicacion?.ejercicios ? `<h3 class="verde">Medicamentos / Tratamiento</h3><p>${lineasLista(indicacion.ejercicios).map(esc).join(" ")}</p>` : ""}
      ${indicacion?.instrucciones ? `<h3 class="verde">Indicaciones</h3><p>${nl2br(indicacion.instrucciones)}</p>` : ""}
      <div class="sep"></div>
      <div class="firmante izq">
        ${firma}
        <div class="rayafirma ancha"></div>
        <div>Fisioterapeuta: ${esc(firmante?.nombre)}</div>
        <div>${esc(firmante?.titulo)}</div>
        ${firmante?.matricula ? `<div>Mat. ${esc(firmante.matricula)}</div>` : ""}
      </div>`;
  }

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${tipo === "certificado" ? "Certificado" : "Indicaciones"} — ${esc(paciente?.nombre)}</title>
  <style>
    @page { size: A4; margin: 18mm 20mm; }
    * { box-sizing: border-box; }
    body { font-family: Helvetica, Arial, sans-serif; color: #111; font-size: 12px; margin: 0; }
    .rosa { color: #d6208a; } .verde { color: #7ac143; }
    h1.clinica { text-align: center; font-size: 26px; margin: 8px 0 4px; } h1.grande { font-size: 30px; margin-top: 30px; }
    .linea-rosa { border-top: 2px solid #d6208a; border-bottom: 1px solid #d6208a; height: 4px; margin-bottom: 18px; }
    h2.titulo { text-align: center; font-size: 22px; margin: 6px 0 0; }
    .sub { font-size: 12px; text-align: center; margin-bottom: 14px; } .centro { text-align: center; }
    .fecha { margin: 8px 0 12px; }
    table.datos { border: 1px solid #ddd; border-collapse: collapse; margin: 6px auto 14px 18px; width: 88%; }
    table.datos th { color: #d6208a; text-align: left; width: 28%; padding: 6px 10px; border-bottom: 1px solid #e5e5e5; background: #fafafa; font-size: 11px; }
    table.datos td { padding: 6px 10px; border-bottom: 1px solid #e5e5e5; border-left: 1px solid #e5e5e5; }
    table.datos2 { margin: 8px 0 18px 30px; } table.datos2 th { text-align: left; width: 120px; padding: 3px 0; font-size: 13px; } table.datos2 td { font-size: 13px; }
    .bloque { border-top: 1px solid #e3e3e3; padding: 7px 0 6px; } .cap { font-size: 10px; color: #777; margin-bottom: 3px; } .item { margin: 5px 0; }
    h3.verde { font-size: 15px; margin: 18px 0 6px; }
    .sep { border-top: 1px solid #bbb; margin: 28px 0 10px; } .sep.negra { border-top: 1px solid #222; margin: 10px 0 14px; }
    .firmante { text-align: center; margin-top: 8px; } .firmante.izq { text-align: left; }
    img.firma { max-height: 80px; max-width: 220px; display: block; margin: 0 auto 2px; } .izq img.firma { margin: 0 0 2px; }
    .rayafirma { border-top: 1px solid #444; width: 260px; margin: 0 auto 4px; } .rayafirma.ancha { width: 330px; margin: 0 0 4px; }
    .chico { font-size: 11px; } .pie { position: fixed; bottom: 0; width: 100%; text-align: center; font-size: 9px; color: #777; }
  </style></head><body>${cuerpo}</body></html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return false;
  w.document.open(); w.document.write(html); w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (_) { /* el usuario puede imprimir manualmente */ } }, 400);
  return true;
}
